import { Text } from "@solomon-labs/design/components/ui";
import React, { useCallback, useMemo, useState } from "react";

import { List } from "../../../components/Data/List";
import { Section } from "../../../components/Section";
import { Button } from "../../../components/ui/button";
import { useSimCursor } from "../../sim-cursor/SimCursor";

import { framable, type FramableProps } from "./framable";
import {
    PolicyCreator,
    type PolicyCreatorControl,
    type PolicyCreatorDemo
} from "./PolicyCreator";
import {
    initialValueFor,
    NEW_USER_DEFAULT_BONUS_AMOUNT,
    NEW_USER_DEFAULT_JOINED_WITHIN_DAYS,
    POLICY_CATALOG,
    serializeNewUserConfig,
    serializeWalletConfig
} from "./policyFixtures";
import { PolicyRow } from "./PolicyRow";
import type { ConfigurablePolicy, NewUserPolicy, Policy } from "./types";
import {
    type SimScriptStep,
    typedNumberBeats,
    useSimScript
} from "./useSimScript";

/**
 * Auto-playing, CURSOR-DRIVEN driver for the bare Policies doc block.
 *
 * The SECOND of the five "+New Program" doc blocks; it copies the
 * cursor-playthrough pattern the Configuration block
 * ({@link ProgramConfigurationSim}) established: hold the block's fixture in
 * local state, render the real props-only Views from it, mount a simulated
 * pointer ({@link useSimCursor}), and script a believable "someone building
 * three policies" playthrough as ordered {@link SimScriptStep} beats — for each
 * control the cursor travels to it (via its `data-sim-cursor-key`), paints its
 * forced HOVER visual, "clicks" it (press pulse + forced press/focus), the
 * value/selection lands, then the cursor moves on. No wallet / RPC /
 * `orgApiFetch` — the block is self-seeded and independent.
 *
 * Interaction wrinkle: the real {@link ProgramPolicies} owns its editor
 * open/type UI state internally (`creating`), so it can't be
 * opened from outside without DOM clicks. Rather than DOM-drive it, this driver
 * LIFTS that state — it composes the same section shell + the REAL
 * {@link PolicyCreator} (in its controlled/`demo` sim mode) + the
 * attached-list ({@link PolicyRow}) directly, with the editor open/type/value
 * and the policies list all held here as fixture state. That gives a clean,
 * reliable cursor-driven auto-play of "open → pick type → configure → add"
 * over real controls with real forced-state visuals.
 *
 * Scripted sequence — showcases the CURRENT policy catalog, leaning on the
 * newer types (Specific Access Control's 3-way mode + New User) rather than the
 * older scalar/tranche pair:
 *   1. Minimum Threshold — create → editor opens on the DEFAULT type (already
 *      selected, no dropdown) → focus the value field → set 2,500 USDv → Add.
 *   2. Specific Access Control — create → the type Select opens + walks + picks
 *      Specific Access Control → the 3-way `[Whitelist|Blacklist|KYC Only]`
 *      mode toggle appears → the cursor clicks "KYC Only" → Add.
 *   3. New User — create → the type Select walks + picks New User → focus the
 *      Bonus amount field → set 25 USDv → Add.
 *   …then settle on the 3-policy list → hide cursor → loop.
 *
 * Drop-in for external (docs/wiki) consumers:
 *
 *   import "@solomon-apps/shared/sim/index.css";
 *   import { ProgramPoliciesSim } from "@solomon-apps/shared/sim";
 *   <ProgramPoliciesSim />
 */

const MINIMUM_THRESHOLD = POLICY_CATALOG.find(
    (p) => p.id === "minimum-threshold"
) as ConfigurablePolicy;
const WALLET_LIST = POLICY_CATALOG.find(
    (p) => p.id === "wallet-list"
) as ConfigurablePolicy;
const NEW_USER = POLICY_CATALOG.find(
    (p) => p.id === "new-user"
) as NewUserPolicy;

/** The New User signup-bonus config the sequence sets (one-time 25 USDv for
 *  customers who joined within 30 days). */
const NEW_USER_VALUE = serializeNewUserConfig({
    bonusAmount: 25,
    joinedWithinDays: 30
});

/** The Specific Access Control config the sequence commits — KYC Only mode
 *  (eligibility gated on KYC-approved status; no manual address list). */
const WALLET_KYC_VALUE = serializeWalletConfig({
    mode: "kyc-only",
    addresses: []
});

/** One attached policy + its value in the fixture list. */
interface AttachedPolicy {
    policy: Policy;
    value: number | string;
}

/** The whole block fixture: which policies are attached, plus the (optional)
 *  open editor's type + in-progress value. */
interface PoliciesFixture {
    attached: AttachedPolicy[];
    /** The type the editor is currently open on, or null when closed. */
    editorType: ConfigurablePolicy | null;
    /** The editor's in-progress value. */
    editorValue: number | string;
}

const EMPTY_FIXTURE: PoliciesFixture = {
    attached: [],
    editorType: null,
    editorValue: ""
};

/** No control focused — the resting demo state (cursor between controls). */
const DEMO_IDLE: PolicyCreatorDemo = { control: null, phase: "hover" };

// ── Per-control beat timing (ms) ────────────────────────────────────────
/** Lead before a control's hover beat — the cursor starts travelling here so
 *  it arrives as the hover paints. Travel itself is distance-derived and
 *  capped at 1s inside the cursor primitive. */
const TRAVEL_MS = 560;
/** How long a control holds its forced HOVER visual before the "click". */
const HOVER_HOLD_MS = 440;
/** How long a control holds its forced PRESS/focus visual (the click). The
 *  value/selection lands on this beat too. */
const PRESS_HOLD_MS = 380;
/** Travel between two ADJACENT options while dragging down an open list. Short
 *  so the descent reads as a smooth drag (each option lit in turn), not a
 *  teleport-per-item. The highlight follows the cursor onto each option. */
const OPTION_STEP_MS = 200;

export interface ProgramPoliciesSimProps extends FramableProps {
    /**
     * Pause (ms) after the sequence settles on its 2-policy end-state before it
     * replays. Defaults to 4500. Set to `-1` to play once and stop.
     */
    loopPauseMs?: number;
}

function ProgramPoliciesSimView({
    loopPauseMs
}: ProgramPoliciesSimProps = {}): React.ReactElement {
    const [fixture, setFixture] = useState<PoliciesFixture>(EMPTY_FIXTURE);
    // Which editor control the cursor is currently painting (create-policy
    // header button lives on the section shell; the rest inside PolicyCreator).
    const [createHover, setCreateHover] = useState<"hover" | "press" | null>(
        null
    );
    const [demo, setDemo] = useState<PolicyCreatorDemo>(DEMO_IDLE);

    const cursor = useSimCursor();
    const {
        moveTo: moveCursor,
        click: clickCursor,
        hide: hideCursor,
        containerRef
    } = cursor;

    const patch = useCallback((next: Partial<PoliciesFixture>): void => {
        setFixture((f) => ({ ...f, ...next }));
    }, []);

    const reset = useCallback((): void => {
        setFixture(EMPTY_FIXTURE);
        setCreateHover(null);
        setDemo(DEMO_IDLE);
        hideCursor();
    }, [hideCursor]);

    // Open the editor on a policy type (with its starting value).
    const openEditor = useCallback(
        (policy: ConfigurablePolicy, value: number | string): void => {
            setFixture((f) => ({
                ...f,
                editorType: policy,
                editorValue: value
            }));
        },
        []
    );

    // Attach the currently-open editor's policy + value, then close the editor.
    const commitEditor = useCallback(
        (policy: ConfigurablePolicy, value: number | string): void => {
            setFixture((f) => ({
                attached: [...f.attached, { policy, value }],
                editorType: null,
                editorValue: ""
            }));
        },
        []
    );

    // ── Beat factories ──────────────────────────────────────────────────
    // The header "+Create Policy" button lives on the section shell, not in
    // PolicyCreator, so its forced state is a separate flag.
    const createPolicyBeats = useCallback(
        (): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: "create:move",
                // Pin travel to the hover's lead (TRAVEL_MS) so arrival == hover.
                run: () =>
                    moveCursor("pol-create-policy", { durationMs: TRAVEL_MS })
            },
            {
                // Paint the hover the instant travel completes (`after` ===
                // TRAVEL_MS) so it lands with the pointer's arrival, no lag.
                after: TRAVEL_MS,
                label: "create:hover",
                run: () => setCreateHover("hover")
            },
            {
                after: HOVER_HOLD_MS,
                label: "create:press",
                run: (): void => {
                    clickCursor();
                    setCreateHover("press");
                }
            }
        ],
        [moveCursor, clickCursor]
    );

    // A PolicyCreator control: travel → hover → press+run. `run` lands the
    // value/selection for this beat (pick a type, set a value, add a tranche,
    // or commit). `cursorKey` targets the control's `data-sim-cursor-key`.
    const editorControlBeats = useCallback(
        (
            control: PolicyCreatorControl,
            cursorKey: string,
            run: () => void
        ): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: `${control}:move`,
                run: (): void => {
                    setCreateHover(null);
                    // Pin travel to the hover's lead so arrival == hover.
                    moveCursor(cursorKey, { durationMs: TRAVEL_MS });
                }
            },
            {
                // Paint the hover the instant travel completes (`after` ===
                // TRAVEL_MS) so it lands with the pointer's arrival, no lag.
                after: TRAVEL_MS,
                label: `${control}:hover`,
                run: () => setDemo({ control, phase: "hover" })
            },
            {
                after: HOVER_HOLD_MS,
                label: `${control}:press`,
                run: (): void => {
                    clickCursor();
                    setDemo({ control, phase: "press" });
                    run();
                }
            }
        ],
        [moveCursor, clickCursor]
    );

    // A value FIELD inside the editor that TYPES its number in digit-by-digit
    // (Minimum Threshold amount, New User bonus): travel → hover → click-to-
    // focus, then the value builds one digit at a time. `seedZero` sets the
    // field's starting (empty) value the moment it's focused; `setValue` maps
    // each typed prefix onto the editor value (a plain number for a scalar
    // policy, or a re-serialized config for New User). `focusControl` is the
    // PolicyCreator control whose forced-focus visual paints (always
    // "config-value" here — the first field carries the cursor key).
    const typedEditorBeats = useCallback(
        (
            cursorKey: string,
            seedZero: () => void,
            target: number,
            setValue: (value: number) => void
        ): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: `${cursorKey}:move`,
                run: (): void => {
                    setCreateHover(null);
                    moveCursor(cursorKey, { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: `${cursorKey}:hover`,
                run: () => setDemo({ control: "config-value", phase: "hover" })
            },
            {
                after: HOVER_HOLD_MS,
                label: `${cursorKey}:focus`,
                run: (): void => {
                    clickCursor();
                    setDemo({ control: "config-value", phase: "press" });
                    seedZero();
                }
            },
            ...typedNumberBeats({
                target,
                startAfter: 220,
                label: cursorKey,
                setValue
            })
        ],
        [moveCursor, clickCursor]
    );

    // The "Policy type" Select: move to trigger → hover → click OPENS the
    // dropdown (options visible, portaled INTO the cursor container) → the
    // cursor DRAGS DOWN the list, lighting each type in turn from the top to the
    // target → click the target → the list closes. Threads the sim-only seams:
    // `open` (controlled-open), `portalContainer` (portal the list where the
    // cursor can reach it), and `highlightType` (paint the highlighted-option
    // visual under the cursor — exactly one, the one under the pointer).
    //
    // Value timing (behave like a real user): the editor opens on the DEFAULT
    // type (Minimum Threshold — the first option), so the trigger label +
    // checkmark read Minimum Threshold while the dropdown opens and the cursor
    // walks down the list. The selected value only flips to `target` on the
    // final click beat that LANDS on the target option — that beat sets the
    // editor's active type + its initial value. For policy 1 (target ===
    // Minimum Threshold) this just confirms the default, consistent with the
    // rest.
    const typeSelectBeats = useCallback(
        (target: ConfigurablePolicy): SimScriptStep[] => {
            const container = (): HTMLElement | null => containerRef.current;
            // Types from the top of the list down to (and including) the target.
            const targetIndex = POLICY_CATALOG.findIndex(
                (p) => p.id === target.id
            );
            const walk = POLICY_CATALOG.slice(0, targetIndex + 1);
            return [
                {
                    after: TRAVEL_MS,
                    label: "type-select:move",
                    run: (): void => {
                        setCreateHover(null);
                        // Pin travel to the hover's lead so arrival == hover.
                        moveCursor("pol-type-select", {
                            durationMs: TRAVEL_MS
                        });
                    }
                },
                {
                    after: TRAVEL_MS,
                    label: "type-select:hover",
                    run: () =>
                        setDemo({ control: "type-select", phase: "hover" })
                },
                {
                    after: HOVER_HOLD_MS,
                    label: "type-select:open",
                    run: (): void => {
                        clickCursor();
                        setDemo({
                            control: "type-select",
                            phase: "press",
                            open: true,
                            portalContainer: container()
                        });
                    }
                },
                // Drag down the list: for each type top→target, travel a short
                // hop onto it, then (on arrival) move the highlight there. Only
                // the type under the cursor is highlighted at any moment.
                ...walk.flatMap((p, i): SimScriptStep[] => {
                    const optKey = `pol-type-opt-${p.id}`;
                    const travel =
                        i === 0 ? HOVER_HOLD_MS + 60 : OPTION_STEP_MS;
                    // The paired highlight beat waits this long before painting;
                    // pin the travel to it so arrival == highlight.
                    const stepMs = i === 0 ? TRAVEL_MS : OPTION_STEP_MS;
                    return [
                        {
                            after: travel,
                            label: `type-select:travel-${p.id}`,
                            run: () =>
                                moveCursor(optKey, { durationMs: stepMs })
                        },
                        {
                            // Arrived on this type — highlight follows.
                            after: i === 0 ? TRAVEL_MS : OPTION_STEP_MS,
                            label: `type-select:highlight-${p.id}`,
                            run: () =>
                                setDemo({
                                    control: "type-select",
                                    phase: "press",
                                    open: true,
                                    portalContainer: container(),
                                    highlightType: p.id
                                })
                        }
                    ];
                }),
                {
                    // Click the target option — the value flips to the target
                    // (checkmark + trigger update) and the dropdown closes.
                    after: HOVER_HOLD_MS,
                    label: "type-select:select",
                    run: (): void => {
                        clickCursor();
                        patch({
                            editorType: target,
                            editorValue: initialValueFor(
                                POLICY_CATALOG,
                                target.id
                            )
                        });
                        setDemo({
                            control: "type-select",
                            phase: "press",
                            open: false
                        });
                    }
                }
            ];
        },
        [moveCursor, clickCursor, containerRef, patch]
    );

    // The Specific Access Control 3-way mode toggle (Whitelist / Blacklist /
    // KYC Only): travel to the target mode's ToggleMenuItem → hover → press +
    // commit the mode into the editor's wallet value. `mode` is the value to
    // land (e.g. "kyc-only"); `commit` writes the serialized wallet config.
    const modeToggleBeats = useCallback(
        (mode: string, commit: () => void): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: `mode-${mode}:move`,
                run: (): void => {
                    setCreateHover(null);
                    moveCursor(`pol-mode-${mode}`, { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: `mode-${mode}:hover`,
                run: () =>
                    setDemo({
                        control: "mode-toggle",
                        phase: "hover",
                        modeTarget: mode
                    })
            },
            {
                after: HOVER_HOLD_MS,
                label: `mode-${mode}:press`,
                run: (): void => {
                    clickCursor();
                    setDemo({
                        control: "mode-toggle",
                        phase: "press",
                        modeTarget: mode
                    });
                    commit();
                }
            }
        ],
        [moveCursor, clickCursor]
    );

    const steps = useMemo<readonly SimScriptStep[]>(
        () => [
            // ── Policy 1 — Minimum Threshold (a scalar value field) ──────
            ...createPolicyBeats(),
            // The editor opens on the DEFAULT type (Minimum Threshold — the
            // first option), already selected, so there's no dropdown dance:
            // create → editor opens on Minimum Threshold → configure → add. The
            // Select trigger + config read Minimum Threshold from the start.
            {
                after: 260,
                label: "open-minimum-threshold",
                run: (): void => {
                    setCreateHover(null);
                    openEditor(
                        MINIMUM_THRESHOLD,
                        initialValueFor(POLICY_CATALOG, MINIMUM_THRESHOLD.id)
                    );
                }
            },
            // travel to the value field (starts EMPTY), focus it, TYPE 2,500
            // (2 → 25 → 250 → 2500) digit-by-digit.
            ...typedEditorBeats(
                "pol-config-value",
                () => patch({ editorValue: "" }),
                2500,
                (value) => patch({ editorValue: value })
            ),
            // Add Policy — the row appears, editor closes.
            ...editorControlBeats("add-policy", "pol-add-policy", () =>
                commitEditor(MINIMUM_THRESHOLD, 2500)
            ),

            // ── Policy 2 — Specific Access Control (3-way mode toggle) ───
            ...createPolicyBeats(),
            // editor opens on the DEFAULT type (Minimum Threshold — the first
            // option), then the Select opens + walks down to Specific Access
            // Control. The value flips to it on the click beat inside
            // typeSelectBeats — like a real user.
            {
                after: 260,
                label: "open-wallet-list",
                run: (): void => {
                    setCreateHover(null);
                    openEditor(
                        MINIMUM_THRESHOLD,
                        initialValueFor(POLICY_CATALOG, MINIMUM_THRESHOLD.id)
                    );
                }
            },
            ...typeSelectBeats(WALLET_LIST),
            // The 3-way `[Whitelist|Blacklist|KYC Only]` mode toggle is now
            // shown (Whitelist is the default). Travel to + click "KYC Only" —
            // the editor value flips to `{ mode: "kyc-only" }` (the manual
            // address list is hidden; the KYC copy shows).
            ...modeToggleBeats("kyc-only", () =>
                patch({ editorValue: WALLET_KYC_VALUE })
            ),
            // Add Policy — the SAC row appears; editor closes.
            ...editorControlBeats("add-policy", "pol-add-policy", () =>
                commitEditor(WALLET_LIST, WALLET_KYC_VALUE)
            ),

            // ── Policy 3 — New User (a signup-bonus policy) ──────────────
            ...createPolicyBeats(),
            {
                after: 260,
                label: "open-new-user",
                run: (): void => {
                    setCreateHover(null);
                    openEditor(
                        MINIMUM_THRESHOLD,
                        initialValueFor(POLICY_CATALOG, MINIMUM_THRESHOLD.id)
                    );
                }
            },
            // Walk the type Select down to New User. The editor opens with BOTH
            // fields EMPTY, so the cursor types each in turn.
            ...typeSelectBeats(NEW_USER),
            // Focus the Bonus amount field + TYPE the one-time 25 USDv bonus
            // (2 → 25). joined-within stays null (empty) while the bonus fills.
            ...typedEditorBeats(
                "pol-config-value",
                () =>
                    patch({
                        editorValue: serializeNewUserConfig({
                            bonusAmount: null,
                            joinedWithinDays: null
                        })
                    }),
                NEW_USER_DEFAULT_BONUS_AMOUNT,
                (value) =>
                    patch({
                        editorValue: serializeNewUserConfig({
                            bonusAmount: value,
                            joinedWithinDays: null
                        })
                    })
            ),
            // Then travel to + focus the "Joined within" field and TYPE its
            // window (3 → 30) — same visible treatment as the bonus (both fields
            // cursor-typed from empty). The bonus (25) is preserved as each
            // joined-within prefix serializes in.
            {
                after: TRAVEL_MS,
                label: "new-user-joined:move",
                run: (): void => {
                    setCreateHover(null);
                    moveCursor("pol-joined-within", { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: "new-user-joined:hover",
                run: (): void =>
                    setDemo({ control: "joined-within", phase: "hover" })
            },
            {
                after: HOVER_HOLD_MS,
                label: "new-user-joined:focus",
                run: (): void => {
                    clickCursor();
                    setDemo({ control: "joined-within", phase: "press" });
                    patch({
                        editorValue: serializeNewUserConfig({
                            bonusAmount: NEW_USER_DEFAULT_BONUS_AMOUNT,
                            joinedWithinDays: null
                        })
                    });
                }
            },
            ...typedNumberBeats({
                target: NEW_USER_DEFAULT_JOINED_WITHIN_DAYS,
                startAfter: 220,
                label: "new-user-joined",
                setValue: (value) =>
                    patch({
                        editorValue: serializeNewUserConfig({
                            bonusAmount: NEW_USER_DEFAULT_BONUS_AMOUNT,
                            joinedWithinDays: value
                        })
                    })
            }),
            // Add Policy — the New User row appears; editor closes.
            ...editorControlBeats("add-policy", "pol-add-policy", () =>
                commitEditor(NEW_USER, NEW_USER_VALUE)
            ),

            // Settle: drop the last control's forced visual + fade the cursor
            // out before the loop pause.
            {
                after: PRESS_HOLD_MS + 200,
                label: "settle",
                run: (): void => {
                    setDemo(DEMO_IDLE);
                    setCreateHover(null);
                    hideCursor();
                }
            }
        ],
        [
            createPolicyBeats,
            editorControlBeats,
            typedEditorBeats,
            typeSelectBeats,
            modeToggleBeats,
            openEditor,
            commitEditor,
            patch,
            moveCursor,
            clickCursor,
            hideCursor
        ]
    );

    useSimScript({ reset, steps, loopPauseMs });

    const { attached, editorType, editorValue } = fixture;
    const editorOpen = editorType !== null;

    // Section header CTA — the REAL "+Create Policy" action, carrying the
    // cursor key + forced hover/press so the scripted pointer can "click" it.
    // Disabled while the editor is open (matches the draft flow where a single
    // editor is open at a time).
    const action = (
        <Button
            variant="sunken"
            size="sm"
            disabled={editorOpen}
            data-sim-cursor-key="pol-create-policy"
            forceHover={createHover === "hover"}
            forcePressed={createHover === "press"}
        >
            +Create Policy
        </Button>
    );

    return (
        <div ref={cursor.containerRef} className="relative">
            <Section
                heading="Policies"
                className="pt-8"
                headerClassName="px-4 mb-6"
                action={action}
            >
                {/* Inline create-policy editor — the REAL PolicyCreator, driven
                    controlled from the lifted fixture (type + value) with the
                    `demo` cursor seam painting one control at a time. */}
                {editorOpen && editorType && (
                    <div className="px-4 mb-4">
                        <PolicyCreator
                            available={POLICY_CATALOG}
                            controlledPolicyId={editorType.id}
                            controlledValue={editorValue}
                            onControlledValueChange={(v) =>
                                patch({ editorValue: v })
                            }
                            demo={demo}
                            onAdd={() => {}}
                            onCancel={() => {}}
                        />
                    </div>
                )}

                {attached.length > 0 && (
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-muted block mt-6 mb-1 px-4"
                    >
                        Added
                    </Text>
                )}
                <List>
                    {attached.map(({ policy, value }) => (
                        <PolicyRow
                            key={policy.id}
                            policy={policy}
                            value={value}
                            onRemove={() => {}}
                        />
                    ))}
                </List>
            </Section>
            {cursor.layer}
        </div>
    );
}

/**
 * Auto-playing Policies doc block. Add `framed` to wrap it in its own
 * fixed-height {@link SimFrame} (the block owns its max height); omit it to
 * render bare.
 */
export const ProgramPoliciesSim = framable(
    ProgramPoliciesSimView,
    "ProgramPoliciesSim"
);
